import React, { Component } from "react"

import { Button, Col, Container, Form, OverlayTrigger, Popover, Row } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown, FaInfoCircle, FaPencilAlt, FaTimes } from "react-icons/fa"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"

import CardRow from "./Cards/CardRow"
import PhoneNumbers from "./Cards/PhoneNumbers"
import {
  hasCachedCollectionTests,
  isProcessingCollection,
  ProcessingCollectionLabel,
} from "../../../api/CachedCollectionTest"
import EmployeeCategoriesGetObservable from "../../../api/LabService/EmployeeCategoriesGet"
import EmployeeCategory from "../../../api/LabService/LabServiceModel/EmployeeCategory"
import RegulatoryMode from "../../../api/LabService/LabServiceModel/RegulatoryMode"
import LabServiceRegulatoryModesGetObservable from "../../../api/LabService/LabServiceRegulatoryModesGet"
import OrderResultRecord from "../../../api/OrderResults/OrderResultModel/OrderResultRecord"
import ServiceDetails from "../../../api/OrderResults/OrderResultModel/ServiceDetails"
import TestEventUpdateData from "../../../api/OrderResults/OrderResultModel/TestEventUpdateData"
import PhoneType from "../../../api/PhoneType"
import { AppState } from "../../../AppState"
import { BuildUserAccess } from "../../../authentication/BuildUserAccess"
import FormatError from "../../../formatters/FormatError"
import GovPerformCollection, { GovPerformCollectionProps } from "../../OrderRequests/GovPerformCollection"
import RandomTestCard from "../RandomTestCard"

enum TestReleaseStatusID {
  PendingCollection = 1,
  CollectionInProcess = 2,
  PendingLaboratoryTesting = 7
}

interface DrugTestProps {
  currentRowInfo: OrderResultRecord
  currentServiceInfo: ServiceDetails
  isOnTrack?: boolean
  editModeChanged: (editMode: boolean) => void
  updateFields: (testEventData: TestEventUpdateData) => void
  onMerge?: () => void
  onHide?: (collected?: boolean) => void
}

interface DrugTestAuthProps {
  canEditPendingOrders?: boolean
  canMergeTests?: boolean
}

interface DrugTestState {
  showPerformCollection: boolean
  editing: boolean
  ccfPersonnelID: string
  ccfName: string
  ccfFirstName: string
  ccfLastName: string
  testEventData: TestEventUpdateData
  availableRegulatoryModes: RegulatoryMode[]
  isRegulatoryModesLoading: boolean
  selectedRegulatoryMode?: RegulatoryMode
  availableCategories: EmployeeCategory[]
  isCategoriesLoading: boolean
  selectedCategory?: EmployeeCategory
}

class DrugTestInfo extends Component<DrugTestProps & DrugTestAuthProps, DrugTestState> {
  testEventData: TestEventUpdateData = {
    PersonnelID: this.props.currentServiceInfo.CCFPersonnelID,
    FirstName: this.props.currentServiceInfo.CCFFirstName,
    LastName: this.props.currentServiceInfo.CCFLastName,
    Phones: {
      [PhoneType.Home]: { number: this.props.currentRowInfo.HomePhone, isRequired: false },
      [PhoneType.Work]: { number: this.props.currentRowInfo.WorkPhone, isRequired: true },
      [PhoneType.Cell]: { number: this.props.currentRowInfo.CellPhone, isRequired: false },
    },
    RegulatoryModeID: this.props.currentServiceInfo.EventData.RegulatoryModeID ?? 0,
    EmployeeCategoryID: this.props.currentServiceInfo.EventData.EmployeeCategoryID ?? 0,
  }

  baseNameState = {
    ccfName: this.props.currentServiceInfo.CCFName,
    ccfFirstName: this.props.currentServiceInfo.CCFFirstName,
    ccfLastName: this.props.currentServiceInfo.CCFLastName
  }

  state: DrugTestState = {
    showPerformCollection: false,
    editing: false,
    ccfPersonnelID: this.props.currentServiceInfo.CCFPersonnelID,
    ...this.baseNameState,
    testEventData: this.testEventData,
    isRegulatoryModesLoading: false,
    isCategoriesLoading: false,
    availableRegulatoryModes: [],
    availableCategories: [],
  }

  subscriptions = new Subscription()

  regulatoryModeTypeahead = React.createRef<Typeahead<RegulatoryMode>>()

  categoryTypeahead = React.createRef<Typeahead<EmployeeCategory>>()

  componentDidMount() {
    const { currentServiceInfo } = this.props
    this.subscriptions.add(
      LabServiceRegulatoryModesGetObservable().subscribe(
        (modes: RegulatoryMode[]) => {
          const curMode = modes.find(
            (el) => el.RegulatoryModeID === (currentServiceInfo.EventData?.RegulatoryModeID || 0)
          )
          this.setState(
            {
              availableRegulatoryModes: modes,
              selectedRegulatoryMode: curMode,
              isRegulatoryModesLoading: false,
            },
            () => this.loadCategories(curMode)
          )
        },
        (error) => {
          toast.error(FormatError(error))
          this.setState({ isRegulatoryModesLoading: false })
        }
      )
    )
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  showPerformCollection = () => {
    this.setState({ showPerformCollection: true })
  }

  closePerformCollection = (collected?: boolean) => {
    this.setState({ showPerformCollection: false }, () => this.props.onHide && this.props.onHide(collected))
  }

  handleToggleEditMode = () => this.setState((prevState) => {
    const isEditNow = !prevState.editing
    this.props.editModeChanged(isEditNow)
    return {
      editing: isEditNow,
      testEventData: this.testEventData,
      ccfPersonnelID: isEditNow ? '' : this.props.currentServiceInfo.CCFPersonnelID,
      selectedRegulatoryMode: this.state.availableRegulatoryModes.find((el) => el.RegulatoryModeID === this.testEventData.RegulatoryModeID),
      selectedCategory: undefined,
      ...this.baseNameState
    }
  }, () => this.loadCategories(this.state.selectedRegulatoryMode))

  handleDonorIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    this.setState((prevState) => {
      return { testEventData: { ...prevState.testEventData, PersonnelID: value.length > 0 ? value : this.props.currentServiceInfo.CCFPersonnelID }, ccfPersonnelID: value }
    },
      () => { this.props.updateFields(this.state.testEventData) }
    )
  }

  handleCcfFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    this.setState((prevState) => {
      return { testEventData: { ...prevState.testEventData, FirstName: value.length > 0 ? value : this.props.currentServiceInfo.CCFFirstName }, ccfFirstName: value }
    },
      () => { this.props.updateFields(this.state.testEventData) }
    )
  }

  handleCcfLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    this.setState((prevState) => {
      return { testEventData: { ...prevState.testEventData, LastName: value.length > 0 ? value : this.props.currentServiceInfo.CCFLastName }, ccfLastName: value }
    },
      () => { this.props.updateFields(this.state.testEventData) }
    )
  }

  handleRegulatoryModeChange = (items: RegulatoryMode[]) => {
    this.setState((prevState) => {
      return {
        testEventData: { ...prevState.testEventData, EmployeeCategoryID: 0, RegulatoryModeID: items.length > 0 ? items[0].RegulatoryModeID : 0 }, 
        selectedRegulatoryMode: items.length > 0 ? items[0] : undefined,
        selectedCategory: undefined,
        availableCategories: items.length > 0 ? prevState.availableCategories : [],
      }
    },
      () => {
        if (items.length > 0) this.loadCategories(items[0])
        this.props.updateFields(this.state.testEventData)
      }
    )

  }

  handleCategoryChange = (items: EmployeeCategory[]) => {
    this.setState((prevState) => {
      return {
        testEventData: { ...prevState.testEventData, EmployeeCategoryID: items.length > 0 ? items[0].EmployeeCategoryID : this.props.currentServiceInfo.EventData.EmployeeCategoryID ?? 0 }, 
        selectedCategory: items.length > 0 ? items[0] : undefined,
      }
    },
      () => this.props.updateFields(this.state.testEventData)
    )
  }

  createGovPerformCollectionProps = (): GovPerformCollectionProps => {
    const {currentRowInfo, currentServiceInfo} = this.props
    const testDetailID: number = 0
    const testDetailIDHash: string = currentRowInfo.TestDetailID || currentServiceInfo.TestDetailID || ""
    const referenceID: number = currentServiceInfo.FormFoxTestAN || 0
    const sampleTypeCode: string = currentServiceInfo.SampleType?.Code || ""
    const sampleTypeID: number = currentServiceInfo.SampleTypeID
    const labCode: string = currentServiceInfo.LabAccountNumber || ""
    const sampleID: string = currentRowInfo.SampleID || currentServiceInfo.SampleID || ""
    const tempInRange: number = currentServiceInfo.TemperatureInRange === undefined ? 0 : currentServiceInfo.TemperatureInRange ? 1 : 2
    const accountID: number = currentRowInfo.CompanyID as number
    const result: GovPerformCollectionProps = {
      testDetailID: testDetailID,
      testDetailIDHash: testDetailIDHash,
      sampleTypeCode: sampleTypeCode,
      sampleTypeID: sampleTypeID,
      referenceID: referenceID,
      labCode: labCode,
      sampleID: sampleID,
      tempInRange: tempInRange,
      personID: currentRowInfo.PersonID,
      collectorNotes: currentServiceInfo.TestDetailDocument?.CollectorNotes,
      onHide: this.closePerformCollection,
      DeviceID: 0,
      AccountID: accountID
    }
    return result
  }

  onPhoneNumberChange = (value: string, type: PhoneType) => {
    this.setState(prev => ({
      ...prev,
      testEventData: {
        ...prev.testEventData,
        Phones: { 
          ...prev.testEventData.Phones,
          [type]: {
            ...prev.testEventData.Phones[type], 
            number: value.replace(/\D/g, "")
          }
        }
      }
    }),
    () => { this.props.updateFields(this.state.testEventData) })
  }

  loadCategories = (regulatoryMode?: RegulatoryMode) => {
    if (!regulatoryMode) return
    this.setState({ isCategoriesLoading: true })
    const { currentServiceInfo } = this.props
    this.subscriptions.add(
      EmployeeCategoriesGetObservable(regulatoryMode.RegulatoryModeID).subscribe(
        (categories: EmployeeCategory[]) => {
          const curCat = categories.find(
            (el) => el.EmployeeCategoryID === (currentServiceInfo.EventData?.EmployeeCategoryID || 0)
          )
          this.setState({
            availableCategories: categories,
            selectedCategory: curCat,
            isCategoriesLoading: false,
          })
        },
        (error) => {
          toast.error(FormatError(error))
          this.setState({ isCategoriesLoading: false })
        }
      )
    )
  }

  render() {
    const { currentRowInfo, currentServiceInfo, isOnTrack, canEditPendingOrders, canMergeTests } = this.props
    const {
      editing,
      isRegulatoryModesLoading,
      availableRegulatoryModes,
      selectedRegulatoryMode,
      isCategoriesLoading,
      availableCategories,
      selectedCategory,
      testEventData: { Phones },
    } = this.state
    const panelInfo = currentServiceInfo.Panel
    const EventData = currentServiceInfo.EventData
    const isPending = currentRowInfo.TestReleaseStatusID === TestReleaseStatusID.PendingCollection
    const isProcessing = currentRowInfo.TestReleaseStatusID === TestReleaseStatusID.CollectionInProcess ||
      currentRowInfo.TestReleaseStatusID === TestReleaseStatusID.PendingLaboratoryTesting
    const isProcessingTest = !hasCachedCollectionTests() || isProcessingCollection(currentRowInfo.TestDetailID)
    const isDrug = currentServiceInfo.TestTypeDescription === "Drug"
    const isEditable = !currentRowInfo.DateCollected
    const performCollectionProps = this.createGovPerformCollectionProps()

    const regEl = (
      <Typeahead
        id="regMode"
        ref={this.regulatoryModeTypeahead}
        labelKey="Name"
        filterBy={(option, props) =>
          props.selected.length > 0 || option?.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
        }
        onChange={this.handleRegulatoryModeChange}
        disabled={isRegulatoryModesLoading || availableRegulatoryModes.length === 0}
        options={availableRegulatoryModes}
        selected={selectedRegulatoryMode ? [selectedRegulatoryMode] : []}
        emptyLabel={isRegulatoryModesLoading ? "LOADING..." : "No Regulatory Modes Found"}
        placeholder="Select"
      >
        <Button
          variant="toggle"
          onClick={() => {
            this.regulatoryModeTypeahead.current?.toggleMenu()
          }}
        >
          <FaAngleDown />
        </Button>
      </Typeahead>
    )

    const validCategory =
      selectedRegulatoryMode === undefined ||
      (!!selectedRegulatoryMode && !isCategoriesLoading && (!!selectedCategory || availableCategories.length === 0))

    const catEl = (
      <Typeahead
        id="category"
        ref={this.categoryTypeahead}
        labelKey="Name"
        filterBy={(option, props) =>
          props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
        }
        onChange={this.handleCategoryChange}
        disabled={isCategoriesLoading || (!isCategoriesLoading && availableCategories.length === 0)}
        isInvalid={!validCategory}
        options={availableCategories}
        selected={selectedCategory ? [selectedCategory] : []}
        emptyLabel={isCategoriesLoading ? "LOADING..." : "No Categories Found"}
        placeholder="Select"
      >
        <Button
          variant="toggle"
          onClick={() => {
            this.categoryTypeahead.current?.toggleMenu()
          }}
        >
          <FaAngleDown />
        </Button>
      </Typeahead>
    )

    return (
      <Container>
        {this.state.showPerformCollection &&
          <GovPerformCollection
            testDetailID={performCollectionProps.testDetailID}
            testDetailIDHash={performCollectionProps.testDetailIDHash}
            referenceID={performCollectionProps.referenceID}
            sampleTypeCode={performCollectionProps.sampleTypeCode}
            sampleTypeID={performCollectionProps.sampleTypeID}
            labCode={performCollectionProps.labCode}
            sampleID={performCollectionProps.sampleID}
            tempInRange={performCollectionProps.tempInRange}
            personID={performCollectionProps.personID}
            collectorNotes={performCollectionProps.collectorNotes}
            onHide={performCollectionProps.onHide}
            DeviceID={currentServiceInfo.ScreenDeviceID || 0}
            AccountID={performCollectionProps.AccountID}
            />
        }

        <Row>
          <Col md={8} className="pt-0">
            <h5>
              {!isOnTrack && <span className="text-uppercase">{currentServiceInfo.TestTypeDescription}</span>} TEST DETAIL <span className="text-uppercase">{currentServiceInfo.CCFName}</span> {currentRowInfo ? currentRowInfo["DonorID"] : ""}
            </h5>
          </Col>
          {!isOnTrack && isEditable && canEditPendingOrders && <Col md={12}>
            <Button variant="icon-link" disabled={currentServiceInfo.IsRandom} className="float-right ml-1 btn-icon-primary" size="sm" onClick={this.handleToggleEditMode} >
              {!this.state.editing && !currentServiceInfo.IsRandom && <FaPencilAlt />}
              {!this.state.editing && currentServiceInfo.IsRandom &&
                <OverlayTrigger placement="top" delay={{ show: 100, hide: 400 }} overlay={
                  <Popover id="defaultDisable">
                    Orders placed in the Selection Inbox may not be edited
                  </Popover>}>
                  <FaPencilAlt />
                </OverlayTrigger>}
              {this.state.editing && <FaTimes />}
            </Button>
          </Col>}
          <Col md={4} className="text-right">
            {isOnTrack && isProcessing && currentRowInfo.FormFoxTestAN && isProcessingTest && <h4>{ProcessingCollectionLabel}</h4>}
            {isOnTrack && isPending && <Button onClick={this.showPerformCollection}>Collect Test Now</Button>}
          </Col>
        </Row>
        <Row className="drug-test-info">
          <Col xs={12} sm={6}>
            <CardRow title="PRIMARY ID" value={`${currentRowInfo.DonorID || ""} (${EventData?.PrimaryIDType})`} />
            {!this.state.editing && <CardRow title="PERSONNEL ID" value={`${currentRowInfo.DonorID || ""} (${EventData?.PrimaryIDType})`} />}
            {this.state.editing && <Row className="p-1">
              <Form.Label column sm={4} className="align-middle" htmlFor="donorId">
                PERSONNEL ID
                <OverlayTrigger placement="top" delay={{ show: 100, hide: 400 }} overlay={
                  <Popover id="defaultDisable">
                    Enter new value to update Primary ID
                  </Popover>}>
                  <FaInfoCircle className="mb-1 ml-2" />
                </OverlayTrigger>
              </Form.Label>
              <Col md={8} className="align-self-center">
                <Form.Control id="donorId" type="text" placeholder={currentRowInfo.DonorID} value={this.state.ccfPersonnelID} onChange={this.handleDonorIdChange} />
              </Col>
            </Row>}
            {!this.state.editing && <CardRow title="NAME" value={currentServiceInfo.CCFName} />}
            {this.state.editing && <Row className="p-1 required">
              <Form.Label column sm={4} className="align-middle" htmlFor="CCFFIrstName">NAME</Form.Label>
              <Col md={8} className="align-self-center">
                <Form.Control id="CCFFirstName" type="text" value={this.state.ccfFirstName} onChange={this.handleCcfFirstNameChange} />
              </Col>
            </Row>}
            {this.state.editing && <Row className="p-1">
              <Form.Label column sm={4} className="align-middle" htmlFor="CCFLastName">&nbsp;</Form.Label>
              <Col md={8} className="align-self-center">
                <Form.Control id="CCFLastName" type="text" value={this.state.ccfLastName} onChange={this.handleCcfLastNameChange} />
              </Col>
            </Row>}
            {!isOnTrack && <PhoneNumbers phones={Phones} readonly={!editing} onChange={this.onPhoneNumberChange} />}
            {!isOnTrack && !editing && <CardRow title="REGULATORY MODE" value={EventData?.RegulatoryModeName} />}
            {!isOnTrack && !editing && <CardRow title="EMPLOYEE CATEGORY" value={EventData?.EmployeeCategoryName} />}
            {!isOnTrack && editing && <CardRow title="REGULATORY MODE" value={regEl} />}
            {!isOnTrack && editing && <CardRow title="EMPLOYEE CATEGORY" value={catEl} />}
            <CardRow title="COMPANY NAME" value={EventData?.CompanyName} />
            {!isOnTrack && <CardRow title="LOCATION" value={EventData?.LocationName} />}
            {!isOnTrack && this.props.currentServiceInfo.LabAccountNumber && isDrug && <CardRow title="LAB ACCOUNT" value={this.props.currentServiceInfo.LabAccountNumber} />}
          </Col>
          <Col xs={12} sm={6} className="summary-block">
            <Row>
              <Col>
                <Button variant="contained">Summary:</Button>
              </Col>
              {!!canMergeTests && <Col className="text-right">
                <Button variant="link" onClick={this.props.onMerge}>Merge</Button>
              </Col>}
            </Row>
            <Container>
              <CardRow title="REASON FOR TEST">
                {currentServiceInfo.IsRandom && <RandomTestCard description={currentServiceInfo.TestReasonDescription} />} 
                {!currentServiceInfo.IsRandom && <>{currentServiceInfo.TestReasonDescription}</> }
              </CardRow>
              {isOnTrack && <CardRow title="Specimen / Reference ID" value={currentServiceInfo.SampleID} />}
              {!isOnTrack && <>
                <Row>
                  <Col xs={6} md={4}>TEST TYPE</Col>
                  <Col xs={6} md={2} className="status-text">{currentServiceInfo.TestTypeDescription}</Col>
                  <Col xs={12} md={6}>
                    <Col xs={6} md={12} className="test-property">Specimen / Reference ID</Col>
                    <Col xs={6} md={12}>{currentServiceInfo.SampleID}</Col>
                  </Col>
                </Row>
                <CardRow title="SAMPLE TYPE" value={currentServiceInfo.SampleTypeDescription} />
                <CardRow title="PANEL" value={panelInfo?.Description || currentServiceInfo.PanelDescription} />
                <CardRow title="REGULATED" value={currentServiceInfo.DOT} bool />
              </>}
              <CardRow title="EVENT STATUS" value={currentServiceInfo.TestReleaseStatusDescription} />
            </Container>
          </Col>
        </Row>
      </Container>
    )
  }
}
function mapStateToProps(state: AppState): DrugTestAuthProps {
  const userAccess = BuildUserAccess(state)
  return {
    canEditPendingOrders: userAccess.canEditPendingOrders,
    canMergeTests: userAccess.canMergeTests
  }
}
const ConnectedDrugTestInfo = connect(mapStateToProps, null)(DrugTestInfo)
export default ConnectedDrugTestInfo
